import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  WorkCentreSummaryTableData,
  WorkCentreTableProps,
} from './WorkCentreSummary.types'
import { Box } from '@mui/material'
import { WORKCENTRE_STATUS } from 'Common/Constants/Constants'
import {
  GridColDef,
  GridRowId,
  GridRenderCellParams,
} from '@mui/x-data-grid-premium'
import { Chip } from '@mui/material'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { WorkcentreSummaryPublishClosure } from 'Common/Components/MUITableToolbar'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'

export const WorkCentreSummaryTable = ({
  data: tableData,
  planId,
  handlePublishWorkcentres,
  isPublishing,
  planAccess,
  isLoading,
}: WorkCentreTableProps) => {
  const navigate = useNavigate()

  const initialVisibleColumns: string[] = [
    'workCentreCode',
    'siteType',
    'siteGroupingLevel3',
    'countSkuKey',
    'avgTotalProductionHours',
    'capacityUtilisation',
    'isCapacityException',
    'avgExcessStockAmount',
    'avgProjectedStockVolume',
    'isStockOutOccasions',
    'isLowStockOccasions',
    'status',
  ]

  // ✅ Create column visibility model for BaseTable
  const columnVisibilityModel = useMemo(() => {
    const allColumns = [
      'baseSourceSiteCode',
      'baseSourceSiteName',
      'siteType',
      'siteGroupingLevel3',
      'workCentreCode',
      'workCentreName',
      'countSkuKey',
      'avgProductionAmount',
      'avgTotalProductionHours',
      'avgAdjustedWorkCentreCapacity',
      'avgDefaultWorkCentreCapacity',
      'capacityAdjustment',
      'isCapacityAdjustment',
      'capacityUtilisation',
      'spareCapacity',
      'isCapacityException',
      'hoursOverCapacity',
      'isConstrainingDifference',
      'avgConstrainingDifference',
      'isExcessStock',
      'avgExcessStockAmount',
      'isShortStock',
      'avgShortStockAmount',
      'avgProjectedStockVolume',
      'avgSafetyStockVolume',
      'avgForecastDemandSignal',
      'avgProjectedStockDuration',
      'avgSafetyStockDuration',
      'isStockOutOccasions',
      'isLowStockOccasions',
      'status',
    ]

    const visibilityModel: Record<string, boolean> = {}
    allColumns.forEach((column) => {
      visibilityModel[column] = initialVisibleColumns.includes(column)
    })
    return visibilityModel
  }, [initialVisibleColumns])
  const columns: ExtendedGridColDef[] = useMemo(() => {
    if (!tableData) {
      return []
    }
    return [
      {
        field: 'baseSourceSiteCode',
        headerName: 'Source site code',
        minWidth: 150,
        type: 'string',
        flex: 1,
      },
      {
        field: 'baseSourceSiteName',
        headerName: 'Source site name',
        minWidth: 150,
        type: 'string',
        flex: 1,
      },
      {
        field: 'siteType',
        headerName: 'Site type',
        minWidth: 150,
        type: 'string',
        flex: 1,
      },
      {
        field: 'siteGroupingLevel3',
        headerName: 'Site grouping level 3',
        minWidth: 180,
        type: 'string',
        flex: 1,
      },
      {
        field: 'workCentreCode',
        headerName: 'Work centre code',
        minWidth: 150,
        type: 'string',
        flex: 1,
      },
      {
        field: 'workCentreName',
        headerName: 'Work centre name',
        minWidth: 150,
        type: 'string',
        flex: 1,
      },
      {
        field: 'countSkuKey',
        headerName: 'SKU count',
        minWidth: 100,
        type: 'number',
        flex: 0.5,
      },
      {
        field: 'avgProductionAmount',
        headerName: 'Production amount',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'avgTotalProductionHours',
        headerName: 'Capacity usage',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'avgAdjustedWorkCentreCapacity',
        headerName: 'Capacity supply',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'avgDefaultWorkCentreCapacity',
        headerName: 'Default supply',
        minWidth: 150,
        type: 'number',
      },
      {
        field: 'capacityAdjustment',
        headerName: 'Total capacity added',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'isCapacityAdjustment',
        headerName: 'Capacity adjustments',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'capacityUtilisation',
        headerName: 'Capacity utilisation',
        minWidth: 150,
        type: 'number',
        flex: 1,
        valueFormatter: (value: number) => {
          if (value == null) {
            return ''
          }
          return `${value?.toLocaleString()} %`
        },
      },
      {
        field: 'spareCapacity',
        headerName: 'Total spare capacity',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'isCapacityException',
        headerName: 'Capacity exceptions',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'hoursOverCapacity',
        headerName: 'Over capacity hours',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'isConstrainingDifference',
        headerName: 'Constrained count',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'avgConstrainingDifference',
        headerName: 'Constraining difference',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'isExcessStock',
        headerName: 'Stock build count',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'avgExcessStockAmount',
        headerName: 'Planned stock build',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'isShortStock',
        headerName: 'Short stock count',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'avgShortStockAmount',
        headerName: 'Planned stock reduction',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'avgProjectedStockVolume',
        headerName: 'Projected stock amount',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'avgSafetyStockVolume',
        headerName: 'Safety stock amount',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'avgForecastDemandSignal',
        headerName: 'Weekly sales rate',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'avgProjectedStockDuration',
        headerName: 'Average stock duration',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'avgSafetyStockDuration',
        headerName: 'Safety stock duration',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'isStockOutOccasions',
        headerName: 'Projected stock outs',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'isLowStockOccasions',
        headerName: 'Low stock warnings',
        minWidth: 150,
        type: 'number',
        flex: 1,
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'singleSelect',
        renderCell: (
          params: GridRenderCellParams<WorkCentreSummaryTableData, string>
        ) => {
          const { value } = params

          if (!value) {
            return <></>
          }

          return value === 'SUCCESS' ? (
            <Chip
              label={value}
              color="success"
              variant="outlined"
              size="small"
            />
          ) : value === 'FAILED' ? (
            <Chip label={value} color="error" variant="outlined" size="small" />
          ) : (
            <Chip
              label={value}
              color="warning"
              variant="outlined"
              size="small"
            />
          )
        },
        width: 150,
      },
    ]
  }, [tableData, initialVisibleColumns])

  const handleRowClick = (params: any) => {
    const { status, baseSourceSiteKey: siteKey, workCentreKey } = params.row

    if (status === WORKCENTRE_STATUS.SUCCESS) {
      navigate(
        `/plan/${planId}/sourcesite/${siteKey}/workcentre/${workCentreKey}`
      )
    }
  }

  // Create custom toolbar with publish functionality
  const createPublishToolbar = ({ selectedRows }: { selectedRows: any }) => {
    const hasWorkcentresSelected = selectedRows?.ids?.size > 0 || false

    // Get selected work centres from the table data based on selected row IDs
    const selectedIds = Array.from(selectedRows?.ids || new Set())
    const selectedWorkcentres =
      tableData?.filter((row) => selectedIds.includes(row.id)) || []

    // Check if all selected workcentres have the same siteGroupingLevel3
    const allSameSiteGrouping = (() => {
      if (selectedWorkcentres.length === 0) return false
      const firstGrouping = selectedWorkcentres[0]?.siteGroupingLevel3
      return selectedWorkcentres.every(
        (wc) => wc.siteGroupingLevel3 === firstGrouping
      )
    })()

    const canPublish = hasWorkcentresSelected && allSameSiteGrouping

    const handlePublishClick = () => {
      handlePublishWorkcentres(selectedWorkcentres)
    }

    return WorkcentreSummaryPublishClosure({
      hasWorkcentresSelected: canPublish,
      isPublishing,
      publichWorkcentres: handlePublishClick,
    })
  }

  // No-op functions for BaseTable required props (read-only table with publish functionality)
  const updateTableData = () => {
    // This is a read-only table, no updates needed
  }

  const uploadData = () => {
    // No upload functionality needed
  }

  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        flex: 'auto',
      }}
    >
      <BaseTable
        data={tableData ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={updateTableData}
        uploadData={uploadData}
        isUploading={false}
        isSaving={false}
        checkBoxSelection={true}
        showTableToolBar={true}
        pinnedColumns={['workCentreCode']}
        downloadFileName="WorkCentreSummary"
        handleRowClick={handleRowClick}
        isRowSelectable={(params: any) =>
          params.row.status === WORKCENTRE_STATUS.SUCCESS
        }
        initialState={{
          // pagination: {
          //   paginationModel: { pageSize: 10 },
          // },
          sorting: {
            sortModel: [{ field: 'workCentreCode', sort: 'asc' }],
          },
          columns: {
            columnVisibilityModel: columnVisibilityModel,
          },
        }}
        sx={{
          border: 0,
          '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
          },
          '&:hover': { cursor: 'pointer' },
        }}
        customToolbar={createPublishToolbar}
      />
    </Box>
  )
}
